/*
 * eslint-disable react/no-array-index-key
 *
 * Index keys are correct for this file. Every list here comes straight from the
 * storm content API, which supplies no stable id on the items - they are plain
 * content objects (url/label, title/detail, name/address/phone). They render
 * read-only, in API order, with no per-item state or inputs, so a key has
 * nothing to preserve across a reorder. Deriving keys from the display text
 * instead would collide the moment an editor entered two items with the same
 * label, which is a worse failure than the one the rule guards against.
 */
/* eslint-disable react/no-array-index-key */
import { IonIcon } from '@ionic/react';
import { callOutline } from 'ionicons/icons';
import React, { useEffect } from 'react';

import CCPage from '../../../components/CCPage';
import { useStoreActions, useStoreState } from '../../../store';
import './storm-pages.css';

const formatUpdated = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const StormResourcesPage: React.FC = () => {
  const entry = useStoreState((s) => s.storm.content.resources);
  const locale = useStoreState((s) => s.translations.language);
  const loadContent = useStoreActions((a) => a.storm.loadContent);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const isSpanish = (locale || '').startsWith('es');
  const body = entry ? (isSpanish ? entry.body_es : entry.body_en) : null;
  const updatedAt = entry?.updated_at || null;

  const content = (
    <div className="storm-page-content">
      {updatedAt && (
        <div className="storm-updated-label">
          {isSpanish ? 'Actualizado: ' : 'Last updated: '}{formatUpdated(updatedAt)}
        </div>
      )}

      {!entry && (
        <div className="storm-empty">
          {isSpanish ? 'Cargando contenido...' : 'Loading content...'}
        </div>
      )}

      {body && (
        <>
          {body.intro && (
            <div
              className="storm-page-html"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: body.intro }}
            />
          )}

          {body.sections && body.sections.length > 0 && body.sections.map((section, si) => (
            <div className="storm-section" key={si}>
              <h2 className="storm-section-title">{section.title}</h2>
              {(section.items || []).map((item, ii) => (
                <div className="storm-card" key={ii}>
                  <div className="storm-card-title">{item.name}</div>
                  {item.address && (
                    <div className="storm-card-meta">
                      <span className="label">{isSpanish ? 'Dirección:' : 'Address:'}</span> {item.address}
                    </div>
                  )}
                  {item.hours && (
                    <div className="storm-card-meta">
                      <span className="label">{isSpanish ? 'Horario:' : 'Hours:'}</span> {item.hours}
                    </div>
                  )}
                  {item.phone && (
                    <div className="storm-card-meta">
                      <IonIcon icon={callOutline} style={{ verticalAlign: 'middle' }} />{' '}
                      <a href={`tel:${item.phone}`}>{item.phone}</a>
                    </div>
                  )}
                  {item.notes && (
                    <div
                      className="storm-page-html"
                      style={{ marginTop: 6 }}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: item.notes }}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );

  return (
    <CCPage
      title={isSpanish ? 'Recursos' : 'Resources'}
      content={content}
    />
  );
};

export default StormResourcesPage;
